/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useCallback, useRef } from "react"
import { useAuthStore } from "./AuthStore"

const Auth: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const {
    isLoggedIn,
    setIsLoggedIn,
    isAuthChecked,
    setIsAuthChecked,
    isAuthorized,
    setIsAuthorized,
    authChecked,
    setAuthChecked,
    logoutReason,
    setLogoutReason,
  } = useAuthStore()

  const inactivityTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const isLogoutPage = useRef(false)
  const esoShimUrl = process.env.REACT_APP_ESOSHIM_URL
  const isESOShimEnabled = process.env.REACT_APP_ESO_SHIM_AUTH_ENABLED === "true"

  const handleLogout = useCallback((reason: "manual" | "inactivity" = "manual") => {
    try {
      // Clear client-side cookies (optional but recommended)
      document.cookie = `auth_token=; expires=Thu, 01 Jan 1970 00:00:00 UTC; domain=${process.env.VITE_COOKIE_DOMAIN || ".airefinery.ciodev.accenture.com"}; path=/;`
      document.cookie = `pkce_verifier=; expires=Thu, 01 Jan 1970 00:00:00 UTC; domain=${process.env.VITE_COOKIE_DOMAIN || ".airefinery.ciodev.accenture.com"}; path=/;`

      // Clear the inactivity timer
      if (inactivityTimer.current) {
        clearTimeout(inactivityTimer.current)
        inactivityTimer.current = null
      }

      // Redirect to server logout endpoint
      window.location.href = `${esoShimUrl}/logout?reason=${reason}`
    } catch (err) {
      console.error("Logout error", err)
    }
  }, [])


  // Add this function to reset the timer on user activity
  const resetInactivityTimer = useCallback(() => {
    if (inactivityTimer.current) {
      clearTimeout(inactivityTimer.current)
    }

    // Set timeout for 10 minutes (600000ms)
    inactivityTimer.current = setTimeout(() => {
      handleLogout("inactivity")
      console.log("User logged out due to inactivity")
    }, 10 * 60 * 1000) // 10 minutes
  }, [handleLogout])


  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const reason = params.get("logoutReason")
    if (reason) {
      setLogoutReason(reason)
      isLogoutPage.current = true
      params.delete("logoutReason")
      const newQuery = params.toString()
      window.history.replaceState({}, "", window.location.pathname + (newQuery
        ? `?${newQuery}`
        : ""))
    }
  }, [])

  // Added: ESO Shim auth validation
  useEffect(() => {
    if (isLogoutPage.current) return

    const validateAndAuthorize = async() => {
      if (!isESOShimEnabled) {
        console.log("Skipping auth check")
        setIsLoggedIn(true)
        setIsAuthChecked(true)
        setIsAuthorized(true)
        setAuthChecked(true)
        return
      }

      try {
      // Step 1: Validate session
        const res = await fetch(`${esoShimUrl}/validate`, {
          method: "GET",
          credentials: "include",
        })

        if (res.status === 200) {
          setIsLoggedIn(true)
          resetInactivityTimer()

          // Step 2: Check Authorization only if validation succeeded
          const rawSiteId = window.location.origin
          let siteId: string

          try {
            siteId = rawSiteId.replace(/^https?:\/\//, "")
          } catch (error) {
            console.error("Error while formatting siteId:", error)
            siteId = rawSiteId
          }

          console.log("siteid is:", siteId)

          const response = await fetch(`${esoShimUrl}/check-auth?siteId=${encodeURIComponent(siteId)}`, {
            method: "GET",
            credentials: "include",
          })

          const result = await response.json()
          console.log("Authorization response:", result)

          if (response.ok && result.authorized === true) {
            setIsAuthorized(true)
          } else {
            console.warn("Authorization failed:", result)
            setIsAuthorized(false)
          }
        } else if (res.status === 401) {
          throw new Error("Unauthorized session")
        } else {
          throw new Error(`Unexpected response status: ${res.status}`)
        }
      } catch (err) {
        console.error("Validation or Authorization failed. Redirecting to ESO Shim.", err)
        const returnUrl = encodeURIComponent(window.location.href)
        window.location.href = `${esoShimUrl}/?returnUrl=${returnUrl}`
      } finally {
        setIsAuthChecked(true)
        setAuthChecked(true)
      }
    }

    validateAndAuthorize()
  }, [resetInactivityTimer])

  // Add event listeners for user activity
  useEffect(() => {
    if (!isLoggedIn) return

    const activityEvents = ["mousemove", "keydown", "click", "scroll", "touchstart"]

    // Set up event listeners
    activityEvents.forEach((event) => {
      window.addEventListener(event, resetInactivityTimer)
    })

    // Start the initial timer
    resetInactivityTimer()

    // Cleanup function
    return () => {
      activityEvents.forEach((event) => {
        window.removeEventListener(event, resetInactivityTimer)
      })

      if (inactivityTimer.current) {
        clearTimeout(inactivityTimer.current)
      }
    }
  }, [isLoggedIn, resetInactivityTimer])


  if (isLogoutPage.current) {
    return (
      <div
        style={{
          width: "100vw",
          height: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: "#f9fafb",
          margin: 0,
          padding: 0,
        }}
      >
        <h1 style={{ fontSize: "2rem", color: "#333" }}>
          {logoutReason === "manual"
            ? "You are signed out of your account."
            : "You are signed out of your account."}
        </h1>
      </div>
    )
  }

  if (!isAuthChecked) {
    return <div>Loading authentication...</div>
  }

  if (!authChecked) {
    return <div>Checking authorization...</div>
  }

  if (!isAuthorized) {
    return <div>You are not authorized to access this application.</div>
  }

  if (!isLoggedIn) {
  // Don't render anything. Redirect to ESO Shim will happen inside useEffect.
    return null
  }

  return <>{children}</>
}

export default Auth
